import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { useSubscription } from '../../context/SubscriptionContext';
import * as messageService from '../../services/messageService';
import { API_BASE_URL } from '../../config/api';
import './ChatModal.css';

const ChatModal = ({ isOpen, onClose, seller, contactPerson }) => {
  const { user } = useAuth();
  const { on, off, joinConversation, leaveConversation, isConnected } = useSocket();
  const { planFeatures, planName: subPlanName, loading: subscriptionLoading } = useSubscription();
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [otherUserPhone, setOtherUserPhone] = useState(null);
  const [otherUserSlug, setOtherUserSlug] = useState(null);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const previousConversationRef = useRef(null);
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const prevMessageCountRef = useRef(0);
  const isUserScrolledUpRef = useRef(false);

  const currentUserId = user?.id;
  const sellerId = seller?.id;

  // Get contact info - prioritize seller prop, then contactPerson
  const contact = seller ? {
    name: seller.name || seller.companyName || 'Supplier',
    role: 'Sales Manager',
    avatar: seller.logo || '/uploads/builder-images/561da124ca9a57c1461a9da91c830f61842922c8_w104.png',
    isVerified: seller.isVerified
  } : contactPerson || {
    name: 'Company Representative',
    role: 'Sales Manager',
    avatar: '/uploads/builder-images/561da124ca9a57c1461a9da91c830f61842922c8_w104.png'
  };

  // Track if user has scrolled up
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      isUserScrolledUpRef.current = scrollHeight - scrollTop - clientHeight > 60;
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [conversationId]);

  // Smart scroll: only on new messages
  useEffect(() => {
    if (!messages || messages.length === 0) {
      prevMessageCountRef.current = 0;
      return;
    }
    const prevCount = prevMessageCountRef.current;
    prevMessageCountRef.current = messages.length;

    if (prevCount === 0 && messages.length > 0) {
      requestAnimationFrame(() => {
        const container = chatContainerRef.current;
        if (container) container.scrollTop = container.scrollHeight;
      });
      isUserScrolledUpRef.current = false;
      return;
    }
    if (messages.length > prevCount && !isUserScrolledUpRef.current) {
      requestAnimationFrame(() => {
        const container = chatContainerRef.current;
        if (container) {
          container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        }
      });
    }
  }, [messages]);

  // Check if user can send messages based on actual subscription features
  const isAdminUser = user?.adminRole === 'admin' || user?.adminRole === 'subadmin';
  const canChat = isAdminUser || planFeatures?.canLiveChat;
  const isBasicPlan = !canChat && !subscriptionLoading;

  // Fetch or create conversation when modal opens
  const initializeConversation = useCallback(async () => {
    if (!isOpen || !currentUserId || !sellerId) return;

    setLoading(true);
    setError(null);
    setMessages([]);

    try {
      // First try to find existing conversation
      const conversations = await messageService.getConversations(currentUserId);
      console.log('Looking for conversation with seller:', sellerId, 'in', conversations.length, 'conversations');
      
      const existingConversation = conversations.find(conv => 
        conv.sellerId === sellerId || 
        conv.buyerId === sellerId ||
        conv.otherUserId === sellerId ||
        conv.sender?.id === sellerId
      );

      if (existingConversation) {
        console.log('Found existing conversation:', existingConversation.id);
        setConversationId(existingConversation.id);
        if (existingConversation.sender?.phone) setOtherUserPhone(existingConversation.sender.phone);
        if (existingConversation.sender?.sellerSlug) setOtherUserSlug(existingConversation.sender.sellerSlug);
        // Fetch messages for this conversation
        const conversationData = await messageService.getMessages(existingConversation.id, currentUserId);
        setMessages(conversationData.messages || []);
        if (conversationData.sender?.phone) setOtherUserPhone(conversationData.sender.phone);
        if (conversationData.sender?.sellerSlug) setOtherUserSlug(conversationData.sender.sellerSlug);
      } else {
        // Basic plan users can only view existing messages, not create new conversations
        if (isBasicPlan) {
          console.log('Basic plan user - no existing conversation found, showing upgrade prompt');
          setShowUpgradePrompt(true);
          setMessages([]);
        } else {
          console.log('Creating new conversation with seller:', sellerId);
          // Create a new conversation
          const newConversation = await messageService.createConversation(
            currentUserId,
            sellerId,
            'chat',
            null,
            null
          );
          setConversationId(newConversation.id || newConversation.conversationId);
          setMessages([]);
        }
      }
    } catch (err) {
      console.error('Error initializing conversation:', err);
      if (err.code === 'MESSAGING_NOT_ALLOWED') {
        setShowUpgradePrompt(true);
      } else {
        setError('Unable to load chat. Please try again.');
      }
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [isOpen, currentUserId, sellerId, isBasicPlan]);

  useEffect(() => {
    initializeConversation();
  }, [initializeConversation]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMessages([]);
      setConversationId(null);
      setError(null);
      setMessage('');
      setShowHeaderMenu(false);
      setOtherUserPhone(null);
      setOtherUserSlug(null);
    }
  }, [isOpen]);

  // WebSocket: Handle real-time message updates
  useEffect(() => {
    if (!currentUserId || !isConnected || !conversationId) return;

    const handleNewMessage = ({ conversationId: msgConvId, message: newMsg }) => {
      if (msgConvId === conversationId) {
        setMessages(prev => {
          // Check if message already exists (by real ID or if it's our optimistic message)
          const messageExists = prev.some(m => 
            m.id === newMsg.id || 
            (m.isOptimistic && m.content === newMsg.content && m.senderId === newMsg.senderId)
          );
          if (messageExists) {
            // If it's an optimistic message being replaced, update it
            return prev.map(m => 
              (m.isOptimistic && m.content === newMsg.content && m.senderId === newMsg.senderId) 
                ? { ...newMsg, isOptimistic: false } 
                : m
            );
          }
          // Only add if sender is not the current user (to avoid duplicates from own sends)
          if (newMsg.senderId === currentUserId) return prev;
          return [...prev, newMsg];
        });
        // Mark as read since user is viewing this conversation
        messageService.markReadStatus(msgConvId).catch(() => {});
      }
    };

    const handleMessagesRead = ({ conversationId: convId }) => {
      if (convId === conversationId) {
        setMessages(prev => prev.map(msg =>
          msg.senderId === currentUserId && msg.status !== 'read'
            ? { ...msg, status: 'read' } : msg
        ));
      }
    };

    const handleMessagesDelivered = ({ conversationId: convId }) => {
      if (convId === conversationId) {
        setMessages(prev => prev.map(msg =>
          msg.senderId === currentUserId && msg.status === 'sent'
            ? { ...msg, status: 'delivered' } : msg
        ));
      }
    };

    on('new_message', handleNewMessage);
    on('messages_read', handleMessagesRead);
    on('messages_delivered', handleMessagesDelivered);

    return () => {
      off('new_message', handleNewMessage);
      off('messages_read', handleMessagesRead);
      off('messages_delivered', handleMessagesDelivered);
    };
  }, [currentUserId, isConnected, conversationId, on, off]);

  // WebSocket: Join/leave conversation rooms
  useEffect(() => {
    if (!isConnected || !conversationId) return;

    if (previousConversationRef.current && previousConversationRef.current !== conversationId) {
      leaveConversation(previousConversationRef.current);
    }

    joinConversation(conversationId);
    previousConversationRef.current = conversationId;

    return () => {
      if (conversationId) {
        leaveConversation(conversationId);
      }
    };
  }, [conversationId, isConnected, joinConversation, leaveConversation]);

  // ============ Profile, Call, Delete handlers ============
  const handleProfileClick = () => {
    const slug = otherUserSlug || seller?.slug;
    if (slug) {
      onClose();
      navigate(`/supplier/${slug}`);
    }
  };

  const handleCallClick = () => {
    const phone = otherUserPhone || seller?.phone || seller?.phoneNumber;
    if (phone) {
      const cleaned = phone.replace(/[\s\-()]/g, '');
      window.location.href = `tel:${cleaned}`;
    }
  };

  const handleDeleteConversation = async () => {
    if (!conversationId || !currentUserId) return;
    if (!window.confirm('Delete this conversation? This cannot be undone.')) return;
    setShowHeaderMenu(false);
    try {
      await messageService.deleteConversation(conversationId, currentUserId);
      onClose();
    } catch (err) {
      console.error('Error deleting conversation:', err);
      setError('Failed to delete conversation.');
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim() || sending || !conversationId || !currentUserId) return;

    // Basic plan users cannot send/reply - show upgrade prompt
    if (isBasicPlan) {
      setShowUpgradePrompt(true);
      return;
    }

    const messageText = message.trim();
    const tempId = `temp-${Date.now()}`;
    setMessage('');
    setSending(true);

    // Optimistically add message to UI
    const optimisticMessage = {
      id: tempId,
      content: messageText,
      senderId: currentUserId,
      createdAt: new Date().toISOString(),
      status: 'sent',
      isOptimistic: true
    };
    setMessages(prev => [...prev, optimisticMessage]);

    try {
      const sentMessage = await messageService.sendMessage(
        conversationId,
        currentUserId,
        messageText
      );
      
      // Replace optimistic message with real one (ensure proper structure)
      const realMessage = {
        id: sentMessage.id,
        content: sentMessage.content || messageText,
        senderId: sentMessage.senderId || currentUserId,
        createdAt: sentMessage.createdAt || new Date().toISOString(),
        status: sentMessage.status || 'delivered',
        isOptimistic: false
      };
      
      setMessages(prev => prev.map(msg => 
        msg.id === tempId ? realMessage : msg
      ));
    } catch (err) {
      console.error('Error sending message:', err);
      // Remove optimistic message on error
      setMessages(prev => prev.filter(msg => msg.id !== tempId));
      setError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // ============ File attachment ============
  const handleFileSelect = async (e, type) => {
    const file = e.target.files[0];
    if (!file || !conversationId || isBasicPlan) return;
    setShowAttachMenu(false);

    if (type === 'image' && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview({ file, url: ev.target.result, name: file.name });
      reader.readAsDataURL(file);
      return;
    }

    setUploadingFile(true);
    try {
      const sentMsg = await messageService.uploadAttachment(conversationId, currentUserId, file);
      if (sentMsg) {
        setMessages(prev => [...prev, { ...sentMsg, isOwn: true }]);
      }
    } catch (error) {
      console.error('File upload error:', error);
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const handleSendImage = async () => {
    if (!imagePreview || !conversationId) return;
    setUploadingFile(true);
    try {
      const sentMsg = await messageService.uploadAttachment(
        conversationId, currentUserId, imagePreview.file, message.trim()
      );
      if (sentMsg) {
        setMessages(prev => [...prev, { ...sentMsg, isOwn: true }]);
      }
      setImagePreview(null);
      setMessage('');
    } catch (error) {
      console.error('Image upload error:', error);
    } finally {
      setUploadingFile(false);
    }
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    
    // If it's already a formatted time string (like "8:00 PM" or "Just now"), return as-is
    if (typeof dateString === 'string' && 
        (dateString.includes('AM') || dateString.includes('PM') || 
         dateString.includes('ago') || dateString === 'Just now')) {
      return dateString;
    }
    
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return '';
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    } catch {
      return '';
    }
  };

  const isOwnMessage = (msg) => {
    return msg.senderId === currentUserId || msg.isSent;
  };

  const renderStatusTicks = (status) => {
    if (!status) return null;
    if (status === 'sent') return <span className="cm-tick cm-tick-sent">&#10003;</span>;
    if (status === 'delivered') return <span className="cm-tick cm-tick-delivered">&#10003;&#10003;</span>;
    if (status === 'read') return <span className="cm-tick cm-tick-read">&#10003;&#10003;</span>;
    return null;
  };

  if (!isOpen) return null;

  return (
    <div className="chat-modal-overlay" onClick={onClose}>
      <div className="chat-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="chat-modal-header">
          <div className="chat-header-content">
            <div className="chat-contact-info" onClick={handleProfileClick} style={{ cursor: (otherUserSlug || seller?.slug) ? 'pointer' : 'default' }}>
              <div className="chat-contact-avatar-container">
                <img 
                  src={contact.avatar} 
                  alt={contact.name} 
                  className="chat-contact-avatar"
                  onError={(e) => {
                    e.target.src = '/uploads/builder-images/561da124ca9a57c1461a9da91c830f61842922c8_w104.png';
                  }}
                />
                {contact.isVerified && (
                  <span className="chat-verified-badge">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#1F396C">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                    </svg>
                  </span>
                )}
              </div>
              <div className="chat-contact-details">
                <span className="chat-contact-name">{contact.name}</span>
                <span className="chat-contact-role">{contact.role}</span>
              </div>
            </div>
            <div className="cm-header-actions">
              {(otherUserPhone || seller?.phone || seller?.phoneNumber) && (
                <button className="cm-header-btn" onClick={handleCallClick} title="Call">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </button>
              )}
              <div className="cm-menu-container">
                <button className="cm-header-btn" onClick={() => setShowHeaderMenu(!showHeaderMenu)} title="More">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/>
                  </svg>
                </button>
                {showHeaderMenu && (
                  <div className="cm-header-menu">
                    {(otherUserSlug || seller?.slug) && (
                      <button onClick={handleProfileClick}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                        </svg>
                        View Profile
                      </button>
                    )}
                    {conversationId && (
                      <button className="cm-menu-delete" onClick={handleDeleteConversation}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                        </svg>
                        Delete Chat
                      </button>
                    )}
                  </div>
                )}
              </div>
              <button className="chat-close-btn" onClick={onClose}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6 6L18 18" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Chat Body */}
        <div className="chat-modal-body">
          {loading ? (
            <div className="chat-loading">
              <div className="chat-loading-spinner"></div>
              <span>Loading messages...</span>
            </div>
          ) : error ? (
            <div className="chat-error">
              <span>{error}</span>
              <button onClick={initializeConversation}>Retry</button>
            </div>
          ) : messages.length === 0 ? (
            <div className="chat-empty">
              <div className="chat-empty-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="#DADADA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <p>Start a conversation</p>
              <span>Send a message to {contact.name}</span>
            </div>
          ) : (
            <div className="chat-messages" ref={chatContainerRef}>
              {messages.map((msg, index) => (
                <div 
                  key={msg.id || index} 
                  className={`chat-message-row ${isOwnMessage(msg) ? 'sent' : 'received'}`}
                >
                  <div className="chat-message-content">
                    {!isOwnMessage(msg) && (
                      <div className="chat-message-avatar-wrapper">
                        <img 
                          src={contact.avatar}
                          alt={contact.name}
                          className="chat-message-avatar"
                          onError={(e) => {
                            e.target.src = '/uploads/builder-images/561da124ca9a57c1461a9da91c830f61842922c8_w104.png';
                          }}
                        />
                      </div>
                    )}
                    <div className="chat-message-bubble-wrapper">
                      {msg.type === 'image' && msg.attachmentUrl ? (
                        <div className={`chat-message-bubble ${isOwnMessage(msg) ? 'sent' : 'received'} cm-image-bubble`}>
                          <img
                            src={getFullUrl(msg.attachmentUrl)}
                            alt="Shared"
                            className="cm-chat-image"
                            onClick={() => window.open(getFullUrl(msg.attachmentUrl), '_blank')}
                          />
                          {msg.content && msg.content !== msg.attachmentName && (
                            <p className="cm-image-caption">{msg.content}</p>
                          )}
                          <span className="chat-message-time cm-img-time">
                            {formatTime(msg.createdAt || msg.time)}
                            {isOwnMessage(msg) && renderStatusTicks(msg.status)}
                          </span>
                        </div>
                      ) : msg.type === 'file' && msg.attachmentUrl ? (
                        <div className={`chat-message-bubble ${isOwnMessage(msg) ? 'sent' : 'received'}`}>
                          <a href={getFullUrl(msg.attachmentUrl)} target="_blank" rel="noopener noreferrer" className="cm-file-attachment">
                            <div className="cm-file-icon">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14 2 14 8 20 8"/>
                              </svg>
                            </div>
                            <div className="cm-file-info">
                              <span className="cm-file-name">{msg.attachmentName || 'File'}</span>
                              <span className="cm-file-size">{formatFileSize(msg.attachmentSize)}</span>
                            </div>
                          </a>
                          {msg.content && msg.content !== msg.attachmentName && (
                            <p className="chat-message-text">{msg.content}</p>
                          )}
                          <span className="chat-message-time">
                            {formatTime(msg.createdAt || msg.time)}
                            {isOwnMessage(msg) && renderStatusTicks(msg.status)}
                          </span>
                        </div>
                      ) : (
                        <>
                          <div className={`chat-message-bubble ${isOwnMessage(msg) ? 'sent' : 'received'} ${msg.isOptimistic ? 'sending' : ''}`}>
                            <p className="chat-message-text">{msg.content || msg.text}</p>
                          </div>
                          <span className="chat-message-time">
                            {formatTime(msg.createdAt || msg.time)}
                            {isOwnMessage(msg) && renderStatusTicks(msg.status)}
                            {msg.isOptimistic && <span className="sending-indicator"> • Sending...</span>}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Image Preview Overlay */}
        {imagePreview && (
          <div className="cm-preview-overlay">
            <div className="cm-preview-header">
              <button className="cm-preview-close" onClick={() => { setImagePreview(null); if (imageInputRef.current) imageInputRef.current.value = ''; }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
              <span className="cm-preview-name">{imagePreview.name}</span>
            </div>
            <div className="cm-preview-body">
              <img src={imagePreview.url} alt="Preview" />
            </div>
            <div className="cm-preview-footer">
              <input
                type="text"
                placeholder="Add a caption..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSendImage(); }}
                className="chat-message-input"
              />
              <button className="chat-send-btn" onClick={handleSendImage} disabled={uploadingFile}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="white"/>
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Footer - Input Area */}
        <div className="chat-modal-footer">
          {isBasicPlan || showUpgradePrompt ? (
            <div className="chat-upgrade-prompt">
              <p>⚠️ {subPlanName ? `${subPlanName.charAt(0).toUpperCase() + subPlanName.slice(1)} plan` : 'Your current plan'} users cannot send messages. <a href="/membership-packages">Upgrade to GCC Business or Global Enterprise</a> to access messaging.</p>
            </div>
          ) : (
          <div className="chat-input-container">
            <div className="cm-attach-container">
              <button className="chat-attach-btn" onClick={() => setShowAttachMenu(!showAttachMenu)} title="Attach file">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16.5 6V17.5C16.5 19.71 14.71 21.5 12.5 21.5C10.29 21.5 8.5 19.71 8.5 17.5V5C8.5 3.62 9.62 2.5 11 2.5C12.38 2.5 13.5 3.62 13.5 5V15.5C13.5 16.05 13.05 16.5 12.5 16.5C11.95 16.5 11.5 16.05 11.5 15.5V6H10V15.5C10 16.88 11.12 18 12.5 18C13.88 18 15 16.88 15 15.5V5C15 2.79 13.21 1 11 1C8.79 1 7 2.79 7 5V17.5C7 20.54 9.46 23 12.5 23C15.54 23 18 20.54 18 17.5V6H16.5Z" fill="#1F396C"/>
                </svg>
              </button>
              {showAttachMenu && (
                <div className="cm-attach-menu">
                  <button onClick={() => { imageInputRef.current?.click(); setShowAttachMenu(false); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                    </svg>
                    Image
                  </button>
                  <button onClick={() => { fileInputRef.current?.click(); setShowAttachMenu(false); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                    Document
                  </button>
                </div>
              )}
              <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleFileSelect(e, 'image')} />
              <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip" style={{ display: 'none' }} onChange={(e) => handleFileSelect(e, 'file')} />
            </div>
            <input
              type="text"
              placeholder="Type message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              className="chat-message-input"
              disabled={loading || !conversationId || uploadingFile}
            />
            <div className="chat-footer-actions">
              <button 
                className="chat-send-btn" 
                onClick={handleSendMessage}
                disabled={!message.trim() || sending || !conversationId || uploadingFile}
              >
                {uploadingFile ? (
                  <div className="cm-spinner" />
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="white"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatModal;
